import { createReducer } from '@reduxjs/toolkit';
import { apiRequestFailed, apiRequestSuccess } from 'services/api/api.actions';
import { forbidden_403, unauthorized_401 } from 'services/api/api.statusCodes';
import convertApiDateTimeToIsoInstant from 'utils/functions/convertApiDateTimeToIsoInstant';
import { addSessionDataInfo } from './session.actions';
import { verifySessionEndpoint } from './session.config';
import { isAnAuthenticatedApiRequest } from './session.helpers';
import { SessionReducerShape } from './session.types';

const initialState: SessionReducerShape = {
  info: {},
  auth: {
    authToken: null,
    from: '',
    verifiedAt: null,
    requestId: '',
  },
};

const sessionDataReducer = createReducer(initialState, (builder) =>
  builder
    .addCase(addSessionDataInfo, (state, { payload: { info, auth } }) => {
      if (info) {
        state.info = { ...state.info, ...info };
      }
      if (auth) {
        state.auth = { ...state.auth, ...auth };
      }
    })
    .addCase(
      apiRequestSuccess,
      (state, { payload: { resourceId, requestConfig, response } }) => {
        if (
          resourceId === verifySessionEndpoint ||
          isAnAuthenticatedApiRequest(requestConfig)
        ) {
          const hasDate = typeof response?.data?.datetime === 'string';
          if (hasDate) {
            state.auth.verifiedAt = convertApiDateTimeToIsoInstant(
              response?.data?.datetime,
            );
          }

          if (resourceId === verifySessionEndpoint) {
            const requestIdHeader = requestConfig.headers?.['x-call-id'];
            state.auth.requestId =
              typeof requestIdHeader === 'string' ? requestIdHeader : '';
          }
          return;
        }
      },
    )
    .addCase(
      apiRequestFailed,
      (state, { payload: { resourceId, error, requestConfig } }) => {
        const isAnAuthTokenRequest =
          resourceId === verifySessionEndpoint ||
          isAnAuthenticatedApiRequest(requestConfig);

        if (!isAnAuthTokenRequest) {
          return;
        }

        const maybeDefinedStatusCode = error?.response?.status;

        // If we do not have a status code it means that the request has failed
        // before reaching the server:
        // either timeout, cors or network error.
        if (typeof maybeDefinedStatusCode !== 'number') {
          return;
        }

        const isTokenInvalid =
          maybeDefinedStatusCode === unauthorized_401 ||
          maybeDefinedStatusCode === forbidden_403;

        if (isTokenInvalid) {
          // We reset the initial state to signal that an error has occurred.
          // This will trigger a redirect.
          state.auth = initialState.auth;

          return;
        }
      },
    ),
);

export default sessionDataReducer;
